import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Station} from '../model/Station';
import {Router} from '@angular/router';
import {Sensor} from '../model/Sensor';
import {StatusComment} from '../station-view/StatusComment';
import {of, shareReplay, take} from 'rxjs';
import {StationViewService} from '../station-view/station-view.service';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {UserRoleProviderService} from "../auth/user-role-provider-service";
import {Role} from '../model/Role';
import {Store} from '@ngrx/store';
import {saveCurrentPageNo, saveCurrentPageSize, saveElementToScroll} from '../filterState/filter-state.actions';
import {StationInfo} from "../model/StationInfo";
import {StationsService} from "./stations.service";
import {ImageService, ImageUrl} from "../images/image.service";
import {findImageUrlForLabel} from "../images/image-label.utils";
import {StationInfosEntry} from "../model/StationInfosEntry";

@Component({
  selector: 'met-stations',
  templateUrl: './stations.component.html',
  styleUrls: ['./stations.component.scss'],
})
export class StationsComponent implements OnInit {
  statusComments: StatusComment[] = StatusComment.getPossibleValues()

  readonly role = Role;

  @Input()
  stations: Station[] = [];
  stationInfos: StationInfo[] = [];
  stationInfoEntries: StationInfosEntry[] = [];

  @Input()
  length: number = 0;

  @Input()
  pageNo: number = 0;
  @Input()
  currentPageSize: number = 0;

  storedCurrentPageSize: number = 150;

  @Output()
  pagingChanged = new EventEmitter<{ pageIndex: number; pageSize: number }>();
  @Input()
  pageSizes: number[] = [];

  defaultIcon = 'assets/default-icon.png';

  imageUrlsOnce$ = of<ImageUrl[]>([]);

  trackByInfoEntry(index: number, entry: StationInfosEntry): string {
    return entry.stationInfo.infoLabel + entry.stationId;
  }

  onPageSizeChange(event: any) {
    this.pagingChanged.emit({pageIndex: event.pageIndex, pageSize: event.pageSize});
    this.storedCurrentPageSize = event.pageSize;
  }

  columnsToDisplayStation: string[] = [];
  trackByStationId = (_: number, s: Station) => s.stationId;
  trackBySensorRow = (index: number, row: SensorRow) =>
    `${row.sensor.sensorId || 'no-id'}-${row.sensor.sensorType || 'no-type'}-${index}`;

  constructor(private router: Router,
              private stationViewService: StationViewService,
              public dialog: MatDialog,
              private userRoleProviderService: UserRoleProviderService,
              private store: Store,
              private stationService: StationsService,
              private imageService: ImageService) {

  }

  ngOnInit(): void {
    this.setColumns();
    if (this.hasRequiredRole(this.role.station_info_view)) {
      this.imageUrlsOnce$ = this.imageService.getStationImageUrls('stationInfo').pipe(take(1), shareReplay(1));
      this.stationService.getStationInfos(0, 10).subscribe(stationInfo => {
        this.stationInfoEntries = stationInfo;
        this.assignStationInfos();
      });
    }
  }

  private setColumns(): void {
    const columns = ['active', 'stationId', 'name', ];

    if(this.hasRequiredRole(this.role.regionaloffice_view)) {
      const regionalCols = ['regionalOffice', 'contactPerson']
      columns.push(...regionalCols);
    }
    if (this.hasRequiredRole(this.role.sensor_view)) {
      const sensorCols = ['sensorId', 'battery', 'sensorType', 'sensorTransmissionSignal', 'statusComment'];
      columns.push(...sensorCols);
    }
    if (this.hasRequiredRole(this.role.notice_view)){
      columns.push('note');
    }
    if (this.hasRequiredRole(this.role.comment_view)){
      columns.push('comment');
    }
    if (this.hasRequiredRole(this.role.image_view)){
      columns.push('images');
    }
    if (this.hasRequiredRole(this.role.station_info_view)) {
      columns.push('info');
    }


    this.columnsToDisplayStation = columns;
  }

  openStationDetails(value: string, pageSize: number, pageNo: number) {
    this.store.dispatch(saveElementToScroll(value))
    this.store.dispatch(saveCurrentPageSize(pageSize))
    this.store.dispatch(saveCurrentPageNo(pageNo));
    this.router.navigate(["station-view", value])
  }

  private emitValueChange() {
    this.pagingChanged.emit({pageIndex: this.pageNo, pageSize: this.currentPageSize});
  }

  setStatusComment(comment: StatusComment, stationId: string, sensor: Sensor) {
    if (stationId != null) {

      this.stationViewService.setSensorStatus(stationId, sensor, comment)
      .pipe(take(1))
      .subscribe(() => this.emitValueChange());
    }
  }

  archiveRemarkNoteOnSensor(sensor: Sensor) {
    this.stationViewService.archiveRemarkNoteOnSensor(sensor);
  }

  archiveRemarkCommentSensor(sensor: Sensor) {
    this.stationViewService.archiveRemarkCommentOnSensor(sensor);
  }

  openDialogNoteSensor(sensor: Sensor, stationId: string, canOpen = true) {
    if(canOpen) this.stationViewService.openDialogNoteOnSensor(sensor, stationId);
  }

  createNoteOnSensor(sensor: Sensor, stationId: string, canOpen = true) {
    if(canOpen) this.stationViewService.createNoteOnSensor(sensor, stationId);
  }

  openDialogCommentSensor(sensor: Sensor, stationId: string, canOpen = true) {
    if (canOpen) this.stationViewService.openDialogCommentOnSensor(sensor, stationId);
  }

  archiveRemarkNoteOnStation(station: Station) {
    this.stationViewService.archiveRemarkNoteOnStation(station);
  }

  archiveRemarkCommentStation(station: Station) {
    this.stationViewService.archiveRemarkCommentOnStation(station);
  }

  openDialogNoteStation(station: Station, canOpen = true) {
    if(canOpen) this.stationViewService.openDialogNoteOnStation(station);
  }

  createNoteOnStation(station: Station, canOpen = true) {
    if(canOpen) this.stationViewService.createNoteOnStation(station);
  }

  openDialogCommentStation(station: Station, canOpen = true) {
    if(canOpen) this.stationViewService.openDialogCommentOnStation(station);
  }

  hasRequiredRole(requiredRole: Role): boolean {
    return this.userRoleProviderService.hasRequiredRole(requiredRole);
  }

 assignStationInfos(): void {
    if (!this.hasRequiredRole(this.role.station_info_view)) {
      return;
    }
    if (!this.stations || !this.stationInfoEntries) return;

    this.stations.forEach(station => {
      if (!station.stationInfoEntrys) {
        station.stationInfoEntrys = [];
      }

      const matchingInfos = this.stationInfoEntries.filter(info =>
        info.stationId === station.id && info.active
      );

      station.stationInfoEntrys.push(...matchingInfos);

    });
  }

  getImageUrlForLabel(infoLabel: string, images: ImageUrl[]): string {
    return findImageUrlForLabel(infoLabel, images, this.defaultIcon);
  }


  onImageError(event: Event) {
    (event.target as HTMLImageElement).src = this.defaultIcon;
  }


  getSortedSensors(station: Station): Sensor[] {
    if (!station.sensors) return [];

    const sensorTypeOrder = ['LA', 'LH', 'LW'];

    return [...station.sensors].sort((a, b) => {

      const sensorIdA = this.normalizeSensorId(a);
      const sensorIdB = this.normalizeSensorId(b);


      const typeA = this.extractSensorType(sensorIdA);
      const typeB = this.extractSensorType(sensorIdB);


      if (!sensorIdA && !sensorIdB) return 0;
      if (!sensorIdA) return 1;
      if (!sensorIdB) return -1;

      if (!typeA && !typeB) {
        return this.compareSensorIds(sensorIdA, sensorIdB);
      }
      if (!typeA) return 1;
      if (!typeB) return -1;


      const indexA = sensorTypeOrder.indexOf(typeA);
      const indexB = sensorTypeOrder.indexOf(typeB);

      if (indexA !== -1 && indexB !== -1) {
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        return this.compareSensorIds(sensorIdA, sensorIdB);
      }

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      if (typeA !== typeB) {
        return typeA.localeCompare(typeB);
      }

      return this.compareSensorIds(sensorIdA, sensorIdB);
    });
  }

  getSensorRows(station: Station): SensorRow[] {
    const sortedSensors = this.getSortedSensors(station);
    const rows: SensorRow[] = [];

    let index = 0;

    while (index < sortedSensors.length) {
      const sensor = sortedSensors[index];
      const sensorId = this.normalizeSensorId(sensor);

      if (!sensorId) {
        rows.push({sensor, displayId: '', isContinuation: false, groupSpan: 1});
        index++;
        continue;
      }

      let span = 1;
      let lookahead = index + 1;
      while (
        lookahead < sortedSensors.length &&
        this.normalizeSensorId(sortedSensors[lookahead]) === sensorId
      ) {
        span++;
        lookahead++;
      }

      rows.push({sensor, displayId: sensorId, isContinuation: false, groupSpan: span});

      for (let i = index + 1; i < lookahead; i++) {
        rows.push({sensor: sortedSensors[i], displayId: '', isContinuation: true, groupSpan: 1});
      }

      index = lookahead;
    }

    return rows;
  }


  extractSensorType(sensorId: string): string {
    if (!sensorId) return '';

    const match = sensorId.match(/(LA|LH|LW)/);
    return match ? match[1] : '';
  }

  compareSensorIds(sensorIdA: string, sensorIdB: string): number {
    if (!sensorIdA && !sensorIdB) return 0;
    if (!sensorIdA) return 1;
    if (!sensorIdB) return -1;


    const numA = parseInt(sensorIdA, 10);
    const numB = parseInt(sensorIdB, 10);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    return sensorIdA.localeCompare(sensorIdB, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  private normalizeSensorId(sensor: Sensor): string {
    return (sensor.sensorId ?? '').trim();
  }

}

interface SensorRow {
  sensor: Sensor;
  displayId: string;
  isContinuation: boolean;
  groupSpan: number;
}
